import type { IAdaptChart } from '~/types/shared-types'
import type { AllowedChartTypes, BaseChartTypes } from './Chart.types'
import {
  prepareChartParamsForType,
} from './Chart.utils'
import type { ChartData, ChartOptions } from 'chart.js'
import type { Component } from 'vue'

export const useCharts = () => {

  const mapChartComponent: Record<AllowedChartTypes, string> = {
    line: 'Line',
    bar: 'Bar',
    barY: 'Bar',
    doughnut: 'Doughnut',
  }

  const prepareChartParams = (chart: IAdaptChart) => {
    return prepareChartParamsForType(chart)
  }

  const ChartComponent = shallowRef<Component | null>(null)
  const normalizedChartData = shallowRef<ChartData<BaseChartTypes> | null>(null)
  const normalizedChartOptions = shallowRef<ChartOptions<BaseChartTypes> | null>(null)

  const loadChartResources = async (
    chartType: AllowedChartTypes,
    chartData?: ChartData<BaseChartTypes>,
    chartOptions?: ChartOptions<BaseChartTypes>,
  ) => {
    ChartComponent.value = null
    normalizedChartData.value = null
    normalizedChartOptions.value = null

    const chartComponentName = mapChartComponent[chartType as keyof typeof mapChartComponent] || 'Line'

    const [chartJsModule, chartUtilsModule] = await Promise.all([
      import('vue-chartjs'),
      import('~/components/Chart/Chart.utils'),
    ])

    const resolvedComponent = chartJsModule[chartComponentName as keyof typeof chartJsModule]
    ChartComponent.value = (resolvedComponent as Component | undefined) ?? null

    switch (chartType) {
      case 'doughnut':
        normalizedChartData.value = (chartData ?? chartUtilsModule.defaultDoughnutChartData) as ChartData<BaseChartTypes>
        normalizedChartOptions.value = (chartOptions ?? chartUtilsModule.defaultDoughnutChartOptions) as ChartOptions<BaseChartTypes>
        break
      case 'bar':
        normalizedChartData.value = (chartData ?? chartUtilsModule.defaultBarChartData) as ChartData<BaseChartTypes>
        normalizedChartOptions.value = (chartOptions ?? chartUtilsModule.defaultBarChartOptions) as ChartOptions<BaseChartTypes>
        break
      case 'barY':
        normalizedChartData.value = (chartData ?? chartUtilsModule.defaultBarChartData) as ChartData<BaseChartTypes>
        normalizedChartOptions.value = (chartOptions ?? chartUtilsModule.defaultBarYChartOptions) as ChartOptions<BaseChartTypes>
        break
      case 'line':
      default:
        normalizedChartData.value = (chartData ?? chartUtilsModule.defaultLineChartData) as ChartData<BaseChartTypes>
        normalizedChartOptions.value = (chartOptions ?? chartUtilsModule.defaultLineChartOptions) as ChartOptions<BaseChartTypes>
        break
    }
  }

  return {
    prepareChartParams,
    ChartComponent,
    normalizedChartData,
    normalizedChartOptions,
    loadChartResources,
  }
}
